import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import type { Site } from './api';
import { useCreateSite, useDeleteSite, useUpdateSite } from './queries';
import { CrmSuggestInput } from './CrmSuggestInput';

interface SitePanelProps {
  customerId: string;
  sites: Site[];
  canWrite: boolean;
}

/**
 * Die Standorte eines Kunden -- F2-02, K-133.
 *
 * Der Hauptstandort ist gekennzeichnet, und es gibt genau einen. Wird ein
 * zweiter gesetzt, verliert der bisherige das Kennzeichen -- das erledigt der
 * Server; die Oberflaeche zeigt nur das Ergebnis.
 *
 * Der Geschaeftszeitenkalender je Standort kommt mit Phase 4. Ein Standort in
 * Wien hat andere Feiertage als einer in Hamburg, und ohne diesen Bezug
 * rechnete die SLA-Uhr beide gleich.
 */
export function SitePanel({ customerId, sites, canWrite }: SitePanelProps) {
  const { t } = useTranslation();
  const create = useCreateSite(customerId);
  const update = useUpdateSite();
  const remove = useDeleteSite();

  const [values, setValues] = useState({
    name: '',
    street: '',
    postal_code: '',
    city: '',
    country: 'DE',
    is_primary: false,
  });

  const set = <K extends keyof typeof values>(field: K, value: (typeof values)[K]) => {
    setValues((current) => ({ ...current, [field]: value }));
  };

  return (
    <div className="stack">
      <section className="card card--flush">
        {sites.length === 0 ? (
          <EmptyState icon="crm" title={t('crm.noSites')} hint={t('crm.noSitesHint')} />
        ) : (
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('masterdata.name')}</th>
                  <th scope="col">{t('crm.address')}</th>
                  {canWrite ? <th scope="col">{t('tickets.actions')}</th> : null}
                </tr>
              </thead>
              <tbody>
                {sites.map((site) => (
                  <tr key={site.id}>
                    <td>
                      {site.name}
                      {site.is_primary ? (
                        <>
                          {' '}
                          <Badge tone="info">{t('crm.primarySite')}</Badge>
                        </>
                      ) : null}
                    </td>
                    <td className="table__muted">
                      {[site.street, `${site.postal_code} ${site.city}`.trim(), site.country]
                        .filter(Boolean)
                        .join(', ')}
                    </td>
                    {canWrite ? (
                      <td>
                        {!site.is_primary ? (
                          <Button
                            variant="ghost"
                            onClick={() => {
                              update.mutate({ siteId: site.id, payload: { is_primary: true } });
                            }}
                          >
                            {t('crm.makePrimary')}
                          </Button>
                        ) : null}
                        <Button
                          variant="ghost"
                          onClick={() => {
                            remove.mutate(site.id);
                          }}
                        >
                          {t('masterdata.delete')}
                        </Button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {update.isError ? <ErrorNotice error={update.error} /> : null}
      {remove.isError ? <ErrorNotice error={remove.error} /> : null}

      {canWrite ? (
        <section className="card">
          <h2>{t('crm.newSite')}</h2>

          <div className="field">
            <label className="field__label" htmlFor="site-name">
              {t('masterdata.name')}
            </label>
            <input
              id="site-name"
              className="field__control"
              value={values.name}
              onChange={(event) => {
                set('name', event.target.value);
              }}
            />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="site-street">
              {t('crm.street')}
            </label>
            <input
              id="site-street"
              className="field__control"
              value={values.street}
              onChange={(event) => {
                set('street', event.target.value);
              }}
            />
          </div>

          <div className="row">
            <div className="field">
              <label className="field__label" htmlFor="site-postal">
                {t('crm.postalCode')}
              </label>
              <input
                id="site-postal"
                className="field__control"
                value={values.postal_code}
                onChange={(event) => {
                  set('postal_code', event.target.value);
                }}
              />
            </div>

            <div className="field">
              <label className="field__label" htmlFor="site-city">
                {t('crm.city')}
              </label>
              <CrmSuggestInput
                id="site-city"
                value={values.city}
                onChange={(next) => {
                  set('city', next);
                }}
                field="site.city"
              />
            </div>

            <div className="field">
              <label className="field__label" htmlFor="site-country">
                {t('crm.country')}
              </label>
              <CrmSuggestInput
                id="site-country"
                value={values.country}
                normalize={(text) => text.toUpperCase().slice(0, 2)}
                onChange={(next) => {
                  set('country', next);
                }}
                field="site.country"
              />
              <span className="field__hint">{t('crm.countryHint')}</span>
            </div>
          </div>

          <label className="field__checkbox">
            <input
              type="checkbox"
              checked={values.is_primary}
              onChange={(event) => {
                set('is_primary', event.target.checked);
              }}
            />
            {t('crm.primarySite')}
          </label>

          {create.isError ? <ErrorNotice error={create.error} /> : null}

          <Button
            variant="primary"
            disabled={!values.name.trim()}
            busy={create.isPending}
            onClick={() => {
              create.mutate(
                { ...values, name: values.name.trim() },
                {
                  onSuccess: () => {
                    setValues({
                      name: '',
                      street: '',
                      postal_code: '',
                      city: '',
                      country: 'DE',
                      is_primary: false,
                    });
                  },
                },
              );
            }}
          >
            {t('masterdata.add')}
          </Button>
        </section>
      ) : null}
    </div>
  );
}
